import Vue from 'vue'
import { ValidationError, RequestError, SearchError } from '@/services/errorHandler.service'
import BackendApi from '@/services/backend.service'
import CustomException from '@/services/customException'
import { VTabReverseTransition } from 'vuetify/lib'

const state = {
  userSearchInput: '',
  isFetchingMessages: false,
  remainingChatTime: -1,
  chatIsNeedReactivate: false,
  messages: [],
  visibleMessagePerPage: 1,
  contextMessage: null,
  galeryMultimediaMessage: null,
  sendInputValue: '',
  sendMessageInputInfo: {
    is_responding: false,
    id: null,
    message_responded: null
  },
  isLoadingTogglePin: false,
  conversationId: null
}

const matchesOmniAndSender = (ticket, omniContactId, senderId) => (
  Number.parseInt(ticket.client.omni_contact_id) === Number.parseInt(omniContactId) &&
  Number.parseInt(ticket.sender?.id) === Number.parseInt(senderId)
)

const matchesPhone = (ticket, phone) => ticket.client.phone === phone

const findTicketInLists = (pinned, unpinned, predicate) => (
  pinned.find(predicate) || unpinned.find(predicate) || null
)

const getters = {
  ticket: (state, getters, rootState) => {
    const { selectedTicketPhone, selectedOmniContactId, selectedSenderId, ticketsByStatus } = rootState.ticketsDrawer

    if (!selectedTicketPhone) return null

    const predicate = selectedOmniContactId && selectedSenderId
      ? (ticket) => matchesOmniAndSender(ticket, selectedOmniContactId, selectedSenderId)
      : (ticket) => matchesPhone(ticket, selectedTicketPhone)

    for (const { pinned, unpinned } of ticketsByStatus) {
      const found = findTicketInLists(pinned, unpinned, predicate)
      if (found) {
        return found
      }
    }

    return null
  },
  statusName: (state, getters) => {
    if (!getters.ticket) return null
    const status_id = getters.ticket.status_id
    const idToStatus = { 1: 'pending', 2: 'in_attention', 3: 'success', 4: 'failed' }
    return idToStatus[status_id]
  },
  visibleMessages: (state) => {
    if (!state.messages) return null
    const MESSAGES_PER_PAGE = 35
    const NUM_VISIBLE_MESSAGES = MESSAGES_PER_PAGE * state.visibleMessagePerPage

    const visibleMessages = state.messages.slice(-NUM_VISIBLE_MESSAGES)

    return visibleMessages
  }
}

const mutations = {
  SET_USER_SEARCH_INPUT(state, payload) {
    state.userSearchInput = payload
  },
  SET_CONTEXT_MESSAGE(state, payload) {
    state.contextMessage = payload
  },
  SET_IS_FETCHING_MESSAGES(state, payload) {
    state.isFetchingMessages = payload
  },
  ADD_VISIBLE_MESSAGES_PAGE(state) {
    state.visibleMessagePerPage++
  },
  SET_VISIBLE_MESSAGES_PAGE(state, payload) {
    state.visibleMessagePerPage = payload
  },
  SET_REMAINING_CHAT_TIME(state, payload) {
    state.remainingChatTime = payload
  },
  SET_MESSAGES(state, payload) {
    state.messages = payload
  },
  SET_CHAT_IS_NEED_REACTIVATE(state, payload) {
    state.chatIsNeedReactivate = payload
  },
  ADD_MESSAGE(state, payload) {
    state.messages.push(payload)
  },
  SET_IS_OPEN_FILE_PREVIEW(state, payload) {
    state.file_preview.is_open = payload
  },
  UPDATE_FILE_PREVIEWED(state, payload) {
    state.file_preview.file_previewed = payload
  },
  ADD_LOCAL_MESSAGE(state, { agent_name, local_message_id, content, customType }) {
    const localMessage = formatLocalMessage({ agent_name, content, customType }, local_message_id)

    state.messages.push(localMessage)
  },
  UPDATE_SEND_INPUT_VALUE(state, payload) {
    state.sendInputValue = payload
  },
  SET_GALERY_MULTIMEDIA_MESSAGE(state, payload) {
    state.galeryMultimediaMessage = payload
  },
  SET_IS_LOADING_TOGGLE_PIN(state, isLoadingTogglePin) {
    state.isLoadingTogglePin = isLoadingTogglePin
  },
  SET_CONVERSATION_ID(state, conversationId) {
    state.conversationId = conversationId
  },
  UPDATE_MESSAGE_ID_BY_LOCAL_ID(state, { message_sent_id, local_message_id }) {
    const message = findMessageById(local_message_id)

    if (message) message.id = message_sent_id
  },
  UPDATE_MESSAGE_REACTION(state, { messageId, reaction }) {
    const message = findMessageById(messageId)

    if (!message) return

    if (!message.content) {
      Vue.set(message, 'content', {})
    }

    Vue.set(message.content, 'reaction', reaction || null)
  }
}

const attachContextToContent = (state, content) => {
  if (!state.contextMessage) {
    return
  }
  const context = state.contextMessage
  context.content.payload.context = null
  content.payload.context = context
}

const buildNewContentFormat = (content) => {
  if (content.type === 'text') {
    return {
      type: content.type,
      expectedFormat: {
        preview_url: true,
        body: content.payload.text
      }
    }
  }
  if (content.type === 'image' || content.type === 'video') {
    return {
      type: content.type,
      expectedFormat: {
        link: content.payload.url,
        caption: content.payload.caption
      }
    }
  }
  if (content.type === 'file') {
    return {
      type: 'document',
      expectedFormat: {
        link: content.payload.url,
        caption: content.payload.caption,
        filename: content.payload.filename
      }
    }
  }
  if (content.type === 'audio') {
    return {
      type: content.type,
      expectedFormat: {
        link: content.payload.url
      }
    }
  }
  if (content.type === 'emoji') {
    return {
      type: 'reaction',
      whatsapp_api_id: content.payload.whatsapp_api_id,
      expectedFormat: {
        message_id: content.payload.message_id,
        emoji: content.payload.emoji
      }
    }
  }
  if (content.type === 'contacts') {
    return {
      type: 'contacts',
      expectedFormat: content.payload.contacts
    }
  }
  if (content.type === 'location') {
    return {
      type: 'location',
      expectedFormat: {
        latitude: content.payload.latitude,
        longitude: content.payload.longitude
      }
    }
  }
  if (content.type === 'product_list') {
    return {
      type: 'interactive',
      expectedFormat: {
        type: 'product_list',
        body: content.payload.body,
        action: {
          catalog_id: '',
          sections: (content.payload.sections || []).map((section) => ({
            title: section.title,
            product_items: (section.product_retailer_ids || []).map((id) => ({
              product_retailer_id: id
            }))
          }))
        }
      }
    }
  }
  if (content.type === 'button' || content.type === 'list') {
    return {
      type: 'interactive',
      expectedFormat: {
        type: content.type
      }
    }
  }
  throw new ValidationError('content type not handled', { content })
}

const toLocalInteractiveContent = (content) => {
  if (content.type === 'button') {
    return attachLocalContext(content, {
      type: 'quick_reply_out',
      payload: {
        content: { text: content.payload.body },
        options: (content.payload.buttons || []).map((button) => ({
          type: 'text',
          title: button.title
        }))
      }
    })
  }
  if (content.type === 'list') {
    const rows = (content.payload.sections || []).reduce((allRows, section) => (
      allRows.concat(section.rows || [])
    ), [])
    return attachLocalContext(content, {
      type: 'list',
      payload: {
        title: content.payload.body,
        items: [{
          options: rows.map((row) => ({
            type: 'text',
            title: row.title
          }))
        }]
      }
    })
  }
  return content
}

const attachLocalContext = (content, localContent) => {
  if (content.payload?.context) {
    localContent.payload.context = content.payload.context
  }
  return localContent
}

const findLastInboundMessage = (messages) => (
  messages.slice().reverse().find((msg) => msg.type === 'inbound') || null
)

const isSameTicketMessage = (currentTicket, newMessage) => {
  const chatTicketId = newMessage.chat_ticket_id
  if (chatTicketId) {
    return Number.parseInt(chatTicketId) === Number.parseInt(currentTicket.id)
  }

  const messagePhone = newMessage.client?.phone
  const currentPhone = currentTicket.client?.phone
  if (!messagePhone || !currentPhone) {
    return false
  }
  if (Number(messagePhone) !== Number(currentPhone)) {
    return false
  }
  if (!currentTicket.client.omni_contact_id || !currentTicket.sender?.id) {
    return true
  }

  const omniChatConversationId = newMessage.omni_chat_conversation_id
  if (!omniChatConversationId) {
    return true
  }

  const currentConversationId = currentTicket.omni_chat_conversation_id || currentTicket.chat?.conversation_id
  if (!currentConversationId) {
    return true
  }

  return Number.parseInt(omniChatConversationId) === Number.parseInt(currentConversationId)
}

const actions = {
  async getMessages({ commit, rootState, getters }, { vueContext, highlightMessageId } = {}) {
    commit('SET_IS_FETCHING_MESSAGES', true)
    commit('SET_MESSAGES', [])

    try {
      const ticket = getters.ticket
      const payload = {
        omni_contact_id: ticket.client.omni_contact_id,
        sender_id: ticket.sender.id
      }

      const response = await BackendApi.post('/chat/all_messages', payload)

      if (!response?.data?.success || !response?.data?.data) {
        throw new RequestError(response)
      }
      const messages = response.data.data.messages || []
      if (highlightMessageId) {
        const MESSAGES_PER_PAGE = 35
        const idx = messages.findIndex(m => String(m.id) === String(highlightMessageId))
        if (idx !== -1) {
          const requiredVisibleMessages = messages.length - idx
          const requiredPages = Math.max(1, Math.ceil(requiredVisibleMessages / MESSAGES_PER_PAGE))
          commit('SET_VISIBLE_MESSAGES_PAGE', requiredPages)
        }
      }
      try {
        commit('SET_CONVERSATION_ID', response.data.data.chatInfo.conversation_id)
        commit('SET_REMAINING_CHAT_TIME', response.data.data.chatInfo.seconds_to_expiration)
        commit('SET_MESSAGES', messages)
      } catch (dataAccessError) {
        await CustomException.handleError({ 
          rootMethod: 'getMessages - Accessing response.data.data properties',
          rootPayload: { 
            payload,
            responseReceived: response,
            responseDataData: response?.data?.data,
            attemptedAccess: 'response.data.data.chatInfo.conversation_id / seconds_to_expiration / messages'
          },
          displayError: false,
          error: dataAccessError
        }, vueContext)
        
        throw dataAccessError
      }

      commit('SET_IS_FETCHING_MESSAGES', false)
    } catch (error) {
      commit('SET_IS_FETCHING_MESSAGES', false)
      CustomException.handleError({ error }, vueContext)
    }
  },
  async sendMessage({ state, commit, rootState, getters }, content, vueContext) {
    try {
      if (!content) {
        throw new ValidationError('No se ha pasado un parámetro', { content })
      }

      attachContextToContent(state, content)

      const { workgroupAgent } = rootState.ticketsDrawer
      const { name: agent_name, user_id } = workgroupAgent

      const local_message_id = Math.random().toString(36).substring(2, 10)

      const apiPayload = {
        provider: workgroupAgent.company_provider,
        metaPhoneId: workgroupAgent.meta_phone_id,
        message: {
          context_id: state.contextMessage?.message_id || null,
          content: {
            type: content.type,
            ...content.payload
          }
        }
      }
      if (content.isSurvey) {
        apiPayload.isSurvey = true
      }
      apiPayload.message.newContentFormat = buildNewContentFormat(content)

      if (content.type !== 'emoji' && state.remainingChatTime <= 0) {
        throw new ValidationError(
          'Han pasado más de 24 horas desde que se ha recibido un mensaje, utilice un template por favor',
          { remainingChatTime: state.remainingChatTime }
        )
      }

      if (content.type !== 'emoji') {
        commit('ADD_LOCAL_MESSAGE', {
          agent_name,
          local_message_id,
          content: toLocalInteractiveContent(content)
        })
        commit('UPDATE_SEND_INPUT_VALUE', '')
        commit('SET_CONTEXT_MESSAGE', null)
      }
      const lastInboundMessage = findLastInboundMessage(state.messages)
      const omniContactId = getters.ticket.client.omni_contact_id
      const senderId = getters.ticket.sender?.id
      
      const response = await BackendApi.post('/meta/whatsapp/messages', { ...apiPayload,
        company: {
          name: workgroupAgent.company_name,
          phone: workgroupAgent.company_phone
        },
        client: {
          name: getters.ticket.client.name,
          phone: getters.ticket.client.phone
        },
        ticket: getters.ticket,
        conversationId: state.conversationId,
        lastInboundMessageId: lastInboundMessage?.id ?? null,
        agentId: workgroupAgent.agent_id,
        workgroupId: rootState.ticketsDrawer.workgroupAgent.workgroup_id,
        omni_contact_id: omniContactId,
        omni_channel_id: getters.ticket.client.current_channel_id,
        sender_id: senderId,
        agent_name: workgroupAgent.name,
        agent_user_id: user_id
      })
      let message
      let message_sent_id
      try {
        message = response.data.data ?? null
        if (!response?.data?.success || !message?.id) {
          throw new RequestError(response)
        }
        message_sent_id = message.id
      } catch (dataAccessError) {
        await CustomException.handleError({ 
          rootMethod: 'sendMessage - Accessing response.data.data',
          rootPayload: { 
            apiPayload,
            ticket: getters.ticket,
            conversationId: state.conversationId,
            responseReceived: response,
            responseDataData: response?.data?.data,
            attemptedAccess: 'response.data.data and message.id'
          },
          displayError: false,
          error: dataAccessError
        }, vueContext)
        throw dataAccessError
      } 

      // quitar l anotification
      const { notifications } = rootState.general

      notifications.forEach((notification) => {
        if (!notification.payload || notification.status === 0) {
          return
        }
        
        const parsedPayload = typeof notification.payload === 'string' 
          ? JSON.parse(notification.payload) 
          : notification.payload
        if (parseInt(parsedPayload?.phone_id) === parseInt(getters.ticket.client.phoneId)) {
          commit('general/SET_STATUS_NOTIFICATION', { notification }, { root: true })
          try {
            const isReadResponse = BackendApi.post('/setNotificationIsReadStatus', { user_notification_id: notification.user_notification_id, newReadStatus: 1, newStatus: 0 })
            if (!isReadResponse?.data?.success) {
              throw new RequestError(isReadResponse)
            }
          } catch (error) {
            console.error('ocurrió un error al actualizar los estaods', error)
          }
        }
      })      
      commit('UPDATE_MESSAGE_ID_BY_LOCAL_ID', { message_sent_id, local_message_id })
    } catch (error) {
      CustomException.handleError({ error }, vueContext)
    }
  },
  async handleNewMessage({ state, commit, getters }, newMessage) {
    const last_message = state.messages[state.messages.length - 1]
    const currentTicket = getters.ticket

    if (!currentTicket) return
    if (newMessage.type !== 'inbound') return
    if (!isSameTicketMessage(currentTicket, newMessage)) return
    if (Number(last_message?.id) === Number(newMessage.id)) return

    const REMAINING_CHAT_TIME_SECONDS = 86400

    commit('SET_REMAINING_CHAT_TIME', REMAINING_CHAT_TIME_SECONDS)
    commit('SET_CHAT_IS_NEED_REACTIVATE', false)
    commit('ADD_MESSAGE', newMessage)
  },
  async handleNewMessageStatus({ commit, state }, new_status_data) {
    const message_id = new_status_data.message.id
    const new_status = new_status_data.message.status

    const message = findMessageByAnyId(message_id)

    if (message) message.status = new_status
  },
  async handleNewMessageReaction({ commit }, new_reaction_data) {
    commit('UPDATE_MESSAGE_REACTION', {
      messageId: new_reaction_data.id,
      reaction: new_reaction_data.reaction
    })
  },
  async togglePinStatus({ commit }, { ticket, vueContext }) {
    if (!ticket) throw new ValidationError('No se ha encontrado el ticket para pinnear/unpinnear')

    commit('SET_IS_LOADING_TOGGLE_PIN', true)
    try {
      const response = await BackendApi.post('/chat/toggle_pin_status', { ticket_id: ticket.id, new_pin_status: !ticket.is_pinned })

      if (!response.data.success) throw new Error('No se ha confirmado el cambio de pin status')

      commit('SET_IS_LOADING_TOGGLE_PIN', false)
      commit('ticketsDrawer/SWITCH_TICKET_PIN_STATUS', { ticket }, { root: true })
    } catch (error) {
      commit('SET_IS_LOADING_TOGGLE_PIN', false)
      CustomException.handleError({ error }, vueContext)
    }
  },
  async sendTypingIndicator({ state, getters }) {
    try {
      const senderId = getters.ticket?.sender?.id
      if (!senderId) return

      let lastInbound = null
      for (let i = state.messages.length - 1; i >= 0; i--) {
        if (state.messages[i].type === 'inbound') {
          lastInbound = state.messages[i]
          break
        }
      }
      const whatsappApiId = lastInbound?.message_id
      if (!whatsappApiId) return

      await BackendApi.post('/chat/typing_indicator', {
        whatsapp_api_id: whatsappApiId,
        sender_id: senderId
      })
    } catch (_) {
      // ponytail: fire-and-forget; typing must not interrupt agent UX
    }
  }
}

function getLastMessageIsFirstOfType(state) {
  if (!state.messages || state.messages.length === 0) return true
  const lastMessage = state.messages[state.messages.length - 1]

  return lastMessage.type !== 'outbound'
}

function getCurrentDate() {
  const currentDate = new Date()

  currentDate.setHours(currentDate.getHours() - 5)
  const current_date_formatted = currentDate.toISOString().substring(0, 19).replace('T', ' ')

  return current_date_formatted
}

function matchesMessageId(message, id) {
  if (id == null) return false

  const idStr = String(id)

  return String(message.id) === idStr || String(message.message_id) === idStr
}

function findMessageById(local_message_id) {
  for (let index = state.messages.length - 1; index >= 0; index--) {
    const message = state.messages[index]

    if (message.id === local_message_id) {
      return message
    }
  }

  return null
}

function findMessageByAnyId(messageId) {
  for (let index = state.messages.length - 1; index >= 0; index--) {
    const message = state.messages[index]

    if (matchesMessageId(message, messageId)) {
      return message
    }
  }

  return null
}

function formatLocalMessage({ agent_name, content, customType }, local_message_id) {
  const message = {
    id: local_message_id ?? 'id_' + Math.random().toString(36).substring(2, 10),
    message_id: null,
    content: content,
    status: 'QUEUED',
    created_at: getCurrentDate(),
    type: customType || 'outbound',
    isFirstOfType: true,
    reaction: null,
    sender: {
      name: agent_name
    }
  }

  return message
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
